import { useQuery } from '@tanstack/react-query';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import { QueryKeys } from '../../api/queryKeys';
import {
  getMCellAlertKeyMCellScore,
  getMCellAlertPoints,
  getMCellAlerts,
  setMCellAlertKeyMCellScore,
  setMCellAlertPoints,
} from './api';
import { MCellAlertsFilter } from './types';

/**
 * Fetches the MCell Alerts matching a filter.
 * @param filter
 * @param enabled
 * @param refetchInterval
 */
export const useMCellAlertsQuery = (
  filter: MCellAlertsFilter,
  enabled: boolean = true,
  refetchInterval?: number,
) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_ALERTS, filter],
    queryFn: () => getMCellAlerts(filter),
    enabled,
    refetchInterval,
    meta: { errorMessageKey: 'errorFetchingWorkCellAlerts' },
  });

/**
 * Fetches the MCell Alert Points of an Inventory Center.
 * @param inventoryCenterId
 */
export const useMCellAlertPointsQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_ALERT_POINTS, inventoryCenterId],
    queryFn: () => getMCellAlertPoints(inventoryCenterId),
    meta: { errorMessageKey: 'errorFetchingWorkCellAlertPoints' },
  });

/**
 * Fetches the Key MCell Score of an Inventory Center (null when it has none).
 * @param inventoryCenterId
 */
export const useMCellAlertKeyMCellScoreQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_ALERT_KEY_MCELL_SCORE, inventoryCenterId],
    // An Inventory Center without one returns 204, which axios reads as ''
    queryFn: async () =>
      (await getMCellAlertKeyMCellScore(inventoryCenterId)) || null,
    meta: { errorMessageKey: 'errorFetchingKeyWorkCellScore' },
  });

/**
 * Sets the MCell Alert Points of an Inventory Center, alert type and level.
 */
export const useSetMCellAlertPointsMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.MCELL_ALERT_POINTS],
    setMCellAlertPoints,
    'errorUpdatingWorkCellAlertPoints',
  );

/**
 * Sets the Key MCell Score points and factor of an Inventory Center.
 */
export const useSetMCellAlertKeyMCellScoreMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.MCELL_ALERT_KEY_MCELL_SCORE],
    setMCellAlertKeyMCellScore,
    'errorUpdatingKeyWorkCellScore',
  );
